import { SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import Pagination from '../../../components/pagination/Pagination';
import ResourceGrid from '../../../components/resource-grid/ResourceGrid';
import ViewToggle from '../../../components/view-toggle/ViewToggle';
import { useBatchedClosures } from '../../../hooks/useBatchedClosures';
import { usePagination } from '../../../hooks/usePagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { getPracticesList } from '../../../services/practices/getPractices';
import { postOfficeHoursList } from '../../../services/practices/postOfficeHoursList';
import { OfficeHour, OfficeHoursApiResponse } from '../../../types';
import { PracticeClosure } from '../../../types/closures/types';
import {
  Practice,
  PracticesResponse,
} from '../../../types/practices/types';
import { applyExternalFilterForNonAdmin } from '../../../utils/externalEntity';
import practiceColumns from '../practices/PracticeColumns';
import { PracticeItem } from '../practices/PracticeItem';

interface GroupedOfficeHours {
  [practice_id: string]: OfficeHour[];
}

const officeHoursCache = new Map<string, OfficeHour[]>();
const officeHoursInFlight = new Map<
  string,
  Promise<GroupedOfficeHours>
>();

interface MarketPracticeListProps {
  marketId: string[];
  practices?: Practice[];
  loading?: boolean;
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
}

const MarketPracticeList = ({
  marketId,
  practices,
  loading,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
}: MarketPracticeListProps) => {
  const [sortOrder, setSortOrder] = useState<
    'ascending' | 'descending'
  >('ascending');
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const toast = useToast();
  const { permissions, isReady } = usePermissions();
  const [officeHoursLoading, setOfficeHoursLoading] = useState(false);
  const [officeHours, setOfficeHours] = useState<GroupedOfficeHours>(
    {},
  );

  const fetchedOfficeHoursRef = useRef<Set<string>>(new Set());

  const usesParentPractices =
    practices !== undefined &&
    loading !== undefined &&
    currentPage !== undefined &&
    totalPages !== undefined &&
    totalCount !== undefined &&
    onPageChange !== undefined;

  const filters = useMemo(
    () => ({ marketIds: marketId }),
    [marketId],
  );

  const {
    items: fetchedPractices,
    loading: fetchedLoading,
    currentPage: fetchedCurrentPage,
    totalPages: fetchedTotalPages,
    totalCount: fetchedTotalCount,
    handlePageChange: fetchedHandlePageChange,
  } = usePagination<Practice, PracticesResponse>(
    useCallback(
      (startToken) =>
        getPracticesList(
          startToken,
          applyExternalFilterForNonAdmin(
            filters,
            permissions.isAdmin,
          ),
          10,
        ),
      [filters, permissions.isAdmin],
    ),
    10,
    {},
    filters,
    isReady && !usesParentPractices,
  );

  const effectivePractices = useMemo<Practice[]>(
    () =>
      usesParentPractices ? (practices ?? []) : fetchedPractices,
    [usesParentPractices, practices, fetchedPractices],
  );
  const effectiveLoading: boolean = usesParentPractices
    ? (loading ?? false)
    : fetchedLoading;
  const effectiveCurrentPage: number = usesParentPractices
    ? (currentPage ?? 1)
    : fetchedCurrentPage;
  const effectiveTotalPages: number = usesParentPractices
    ? (totalPages ?? 1)
    : fetchedTotalPages;
  const effectiveTotalCount: number = usesParentPractices
    ? (totalCount ?? 0)
    : fetchedTotalCount;
  const effectiveOnPageChange: (page: number) => void =
    usesParentPractices
      ? (onPageChange ?? (() => undefined))
      : fetchedHandlePageChange;

  const practicesWithOfficeHours = useMemo(() => {
    return effectivePractices.map((practice) => ({
      ...practice,
      office_hours:
        officeHours[practice.id] || practice.office_hours || [],
    }));
  }, [effectivePractices, officeHours]);

  const practiceIds = useMemo(
    () => effectivePractices.map((practice) => practice.id),
    [effectivePractices],
  );
  const { closuresByPractice, isLoading: closuresLoading } =
    useBatchedClosures(practiceIds);

  const getClosuresForPractice = useCallback(
    (practiceId: string): PracticeClosure[] | null | undefined => {
      if (
        Object.prototype.hasOwnProperty.call(
          closuresByPractice,
          practiceId,
        )
      ) {
        return closuresByPractice[practiceId];
      }

      // While the batched fetch is still in progress, suppress child fallback.
      if (closuresLoading) {
        return null;
      }

      // After loading, allow child fallback fetch for missing entries.
      return undefined;
    },
    [closuresByPractice, closuresLoading],
  );

  const filteredAndSortedPractices = useMemo(() => {
    let filtered = practicesWithOfficeHours;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = practicesWithOfficeHours.filter((practice) => {
        const name = practice.name?.toLowerCase() || '';
        const address1 = practice.address1?.toLowerCase() || '';
        const address2 = practice.address2?.toLowerCase() || '';
        const city = practice.city?.toLowerCase() || '';
        const state = practice.state?.toLowerCase() || '';
        const fullAddress =
          `${address1} ${address2} ${city} ${state}`.toLowerCase();
        return name.includes(query) || fullAddress.includes(query);
      });
    }

    return filtered.sort((a, b) => {
      const nameA = a.name?.toUpperCase() ?? '';
      const nameB = b.name?.toUpperCase() ?? '';
      return sortOrder === 'ascending'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
  }, [practicesWithOfficeHours, searchQuery, sortOrder]);

  const handlePracticeClick = useCallback(
    (practice: Practice) => {
      navigate(`/practices/${practice.id}`);
    },
    [navigate],
  );

  const groupByPracticeId = useCallback(
    (response: OfficeHoursApiResponse): GroupedOfficeHours => {
      return response.office_hours.reduce(
        (acc: GroupedOfficeHours, curr: OfficeHour) => {
          const key = curr.practice_id;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(curr);
          return acc;
        },
        {},
      );
    },
    [],
  );

  const fetchOfficeHours = useCallback(
    async (ids: string[]) => {
      const cached: GroupedOfficeHours = {};
      const uncached: string[] = [];

      ids.forEach((id) => {
        if (officeHoursCache.has(id)) {
          cached[id] = officeHoursCache.get(id) || [];
        } else {
          uncached.push(id);
        }
      });

      if (Object.keys(cached).length > 0) {
        setOfficeHours((prev) => ({ ...prev, ...cached }));
        fetchedOfficeHoursRef.current = new Set([
          ...fetchedOfficeHoursRef.current,
          ...Object.keys(cached),
        ]);
      }

      if (uncached.length === 0) {
        return;
      }

      try {
        setOfficeHoursLoading(true);
        const batchKey = [...uncached].sort().join(',');
        let request = officeHoursInFlight.get(batchKey);

        if (!request) {
          request = postOfficeHoursList(uncached)
            .then((data) => {
              const grouped = groupByPracticeId(data.data);
              uncached.forEach((id) => {
                officeHoursCache.set(id, grouped[id] || []);
              });
              return grouped;
            })
            .finally(() => {
              officeHoursInFlight.delete(batchKey);
            });

          officeHoursInFlight.set(batchKey, request);
        }

        const groupedOfficeHours = await request;
        setOfficeHours((prev) => ({
          ...prev,
          ...groupedOfficeHours,
        }));
        fetchedOfficeHoursRef.current = new Set([
          ...fetchedOfficeHoursRef.current,
          ...uncached,
        ]);
      } catch {
        toast({
          title: 'Failed to fetch office hours',
          description: 'Please try again',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setOfficeHoursLoading(false);
      }
    },
    [groupByPracticeId, toast],
  );

  useEffect(() => {
    const newPracticeIds = practiceIds.filter(
      (id) => !fetchedOfficeHoursRef.current.has(id),
    );

    if (newPracticeIds.length > 0 && !officeHoursLoading) {
      fetchOfficeHours(newPracticeIds);
    }
  }, [practiceIds, officeHoursLoading, fetchOfficeHours]);

  if (effectiveLoading) {
    return (
      <Box ml="2rem" mr="2rem" mb="3rem" mt="1.75rem">
        <PageLoader height="45vh" />
      </Box>
    );
  }

  const showEmptyState =
    effectivePractices.length === 0 && !effectiveLoading;
  const showNoSearchResults =
    filteredAndSortedPractices.length === 0 && searchQuery.trim();

  return (
    <Box mb="3rem" mt="1.75rem">
      <Flex
        justifyContent={'space-between'}
        alignItems={'center'}
        mb="1rem"
      >
        <Text
          fontSize={'1.25rem'}
          fontWeight={'700'}
          lineHeight={'120%'}
          color="text.secondary"
        >
          Practices
        </Text>
        <Flex gap="0.25rem" alignItems="center">
          <InputGroup width="18.75rem">
            <InputLeftElement
              pointerEvents="none"
              h="2.5rem"
              pl="1rem"
            >
              <SearchIcon color="gray.500" boxSize="1rem" />
            </InputLeftElement>
            <Input
              placeholder="Search practices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="#F7FAFC"
              borderColor="transparent"
              height="2.5rem"
              fontSize="0.875rem"
              pl="2.5rem"
              pr="1rem"
              borderRadius="0.375rem"
              _placeholder={{ color: 'gray.500' }}
              _focus={{
                borderColor: 'blue.500',
                boxShadow: '0 0 0 1px #3182ce',
                bg: 'white',
              }}
              _hover={{ borderColor: 'gray.300', bg: 'white' }}
            />
          </InputGroup>
          <Select
            bg="#F7FAFC"
            width="10rem"
            height="2.5rem"
            fontSize="0.875rem"
            _hover={{ bg: 'white' }}
            _active={{ transform: 'scale(0.98)' }}
            _focus={{ boxShadow: 'outline' }}
            borderRadius="0.375rem"
            border="1px solid transparent"
            value={sortOrder}
            onChange={(e) =>
              setSortOrder(
                e.target.value as 'ascending' | 'descending',
              )
            }
          >
            <option value="ascending">Name A - Z</option>
            <option value="descending">Name Z - A</option>
          </Select>
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
        </Flex>
      </Flex>

      {showEmptyState ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          minH="300px"
          textAlign="center"
          mt="2rem"
        >
          <Text
            fontSize="1.25rem"
            fontWeight="600"
            color="text.secondary"
            mb="0.5rem"
          >
            No Practices Found
          </Text>
          <Text
            fontSize="0.875rem"
            color="gray.600"
            mb="1.5rem"
            maxW="400px"
          >
            Use "Manage Practices" under this market to add/remove
            practices.
          </Text>
          <Button
            colorScheme="blue"
            size="md"
            onClick={() =>
              navigate(`/markets/${marketId[0]}/practices`)
            }
            _hover={{ transform: 'translateY(-1px)' }}
            transition="all 0.2s ease-in-out"
          >
            Manage Practices
          </Button>
        </Flex>
      ) : showNoSearchResults ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          minH="300px"
          textAlign="center"
          mt="2rem"
        >
          <Text
            fontSize="1.25rem"
            fontWeight="600"
            color="text.secondary"
            mb="0.5rem"
          >
            No practices match your search
          </Text>
          <Text
            fontSize="0.875rem"
            color="gray.600"
            mb="1.5rem"
            maxW="400px"
          >
            Try adjusting your search terms or clear the search to see
            all practices.
          </Text>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSearchQuery('')}
            _hover={{ transform: 'translateY(-1px)' }}
            transition="all 0.2s ease-in-out"
          >
            Clear Search
          </Button>
        </Flex>
      ) : viewMode === 'table' ? (
        <>
          <ResourceGrid<Practice>
            data={filteredAndSortedPractices}
            columns={practiceColumns}
          />
          {!effectiveLoading && effectiveTotalCount > 0 && (
            <Text
              fontSize="0.875rem"
              color="gray.600"
              mb="1rem"
              textAlign="center"
            >
              Showing {(effectiveCurrentPage - 1) * 10 + 1}–
              {Math.min(
                effectiveCurrentPage * 10,
                effectiveTotalCount,
              )}{' '}
              of {effectiveTotalCount}
            </Text>
          )}
          {effectiveTotalPages > 1 && (
            <Pagination
              currentPage={effectiveCurrentPage}
              totalPages={effectiveTotalPages}
              onPageChange={effectiveOnPageChange}
            />
          )}
        </>
      ) : (
        <>
          <Stack gap={'1.5rem'} mb={'2.31rem'}>
            {filteredAndSortedPractices.map((practice, index) => (
              <PracticeItem
                data-testid={`practice-item-${practice?.id}`}
                key={`${practice?.id}-${index}`}
                practice={practice}
                onClick={() => handlePracticeClick(practice)}
                officeHoursLoading={officeHoursLoading}
                clinicClosures={getClosuresForPractice(practice.id)}
                isClosuresLoading={closuresLoading}
              />
            ))}
          </Stack>
          {!effectiveLoading && effectiveTotalCount > 0 && (
            <Text
              fontSize="0.875rem"
              color="gray.600"
              mb="1rem"
              textAlign="center"
            >
              Showing {(effectiveCurrentPage - 1) * 10 + 1}–
              {Math.min(
                effectiveCurrentPage * 10,
                effectiveTotalCount,
              )}{' '}
              of {effectiveTotalCount}
            </Text>
          )}
          {effectiveTotalPages > 1 && (
            <Pagination
              currentPage={effectiveCurrentPage}
              totalPages={effectiveTotalPages}
              onPageChange={effectiveOnPageChange}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default MarketPracticeList;
